/**
 * "Sign off your crew's hours" — the second tour, and the one that proved the engine needed a role
 * gate.
 *
 * WHO IT IS FOR, AND WHY `role` AND NOT `permission`. Everything else in this engine gates on
 * hasPermission, on purpose. This tour cannot, and the reason is worth stating once: the Crew Hours
 * tab is rendered off a literal string comparison — WeeklyTimeView.jsx:25,
 * `(user.role || '').toLowerCase() === 'foreman'` — while useAuth.hasPermission returns true for
 * EVERY permission when the role is admin or master_admin (useAuth.jsx:346-351). So no permission
 * string can describe "the people who actually have this tab", and an admin offered this tour would
 * reach step 4 and be told to click a control that is not on their screen.
 *
 * A MANAGER IS AUTHORISED FOR THIS AND STILL CANNOT DO IT. GET /employee-hours/crew accepts
 * foreman | timekeeper | manager | admin | master_admin (timekeeping backend
 * api/routes/employee_hours.py:93-95), but the frontend only ever renders the tab for a foreman.
 * That is a real gap in the app, not in the tour, and the tour follows what is on screen. If the
 * owner ever widens the tab, widening this tour is a one-word edit here.
 *
 * WHERE CREW HOURS COME FROM, because the copy depends on it: a time block only appears in this
 * screen once the daily report carrying it reaches timekeeper_reviewed / approved / pm_approved /
 * super_approved (employee_hours.py:113-121). So this tour picks up exactly where the daily-report
 * tour puts it down — write the report, the office reviews it, then the hours land here to be
 * signed off.
 *
 * WHAT APPROVING ACTUALLY DOES. It sets employee_acknowledged and shows the office a green
 * "✓ Acknowledged" against that line in their review screen (ReportReview.jsx:206-214). It is a
 * sign-off record, NOT a gate on payroll — nothing downstream refuses to pay an unacknowledged
 * block. An earlier draft of step 8 said it was "the last thing between their hours and payroll";
 * grepping employee_acknowledged showed that is false, and a tour that overstates what a button
 * does teaches a new hire to distrust the next thing it says.
 *
 * STEPS 5-8 LIVE BEHIND A CLICK, NOT A URL. WeeklyTimeView keeps the My Hours / Crew Hours split in
 * useState, so /timekeeping/my-hours is two screens at one address and the engine's route sync
 * cannot reach the second one. Step 4 has to be performed to get there, and it is now GATED on the
 * crew panel appearing rather than on the click that was meant to open it — a user who pressed Next
 * instead used to land on four steps whose anchors are genuinely absent, each of which then timed
 * out and skipped itself. Each of steps 5-8 still declares a `screen` of its own so the anti-rot
 * guard checks them on the crew tab rather than on the tab the page happens to open on.
 *
 * ── WHY THIS TOUR IS A PRACTICE RUN AND WHAT THAT COSTS ─────────────────────────────────────────
 * Approving is a sign-off against somebody's paid hours, and the point of a walkthrough is that a
 * new foreman presses the real button. Those two cannot both be true unless the write is stopped, so
 * this tour runs in practice mode like every other one: the approve request is answered inside the
 * browser and no hours are signed for.
 *
 * That answer had to be written down (shared/tour/practiceResponses.js) rather than left to the
 * generic one. This screen REPLACES the block it approved with whatever the server sends back, so a
 * generic answer — an id and a timestamp, no employee, no date, no hours — did not merely look odd:
 * it regrouped under an undefined employee and took the page down with
 * `emp.employee_name.charAt(0)`. A trainee pressing Approve got a white screen.
 */
import { defineTour, AUDIENCE, ADVANCE_ON } from '../tourSchema'

/** The crew tab is not a route. See the block comment above. */
const CREW_TAB = '/timekeeping/my-hours#crew'

export default defineTour({
  id: 'crew-time-signoff',
  title: 'Sign off your crew’s hours',
  summary: 'Check a week of your crew’s time and approve it.',
  audience: AUDIENCE.ONBOARDING,
  module: 'timekeeping',
  role: 'foreman',

  steps: [
    {
      id: 'open-hours',
      anchor: 'timekeeping.nav.my-hours',
      route: '/timekeeping',
      module: 'timekeeping',
      title: 'Start here',
      body: 'Hours you write on a daily report don’t count as agreed until someone confirms them. This tab is where you confirm your own — and, because you run a crew, theirs too. Open it.',
      advanceOn: ADVANCE_ON.DO,
      until: { appears: 'crew-time.week' },
    },
    {
      id: 'pick-the-week',
      anchor: 'crew-time.week',
      route: '/timekeeping/my-hours',
      module: 'timekeeping',
      title: 'You work one week at a time',
      body: 'A week here runs Monday to Sunday. The arrows step back or forward a week, and This Week jumps you to the one you’re in. Last week is usually the one you’re chasing.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'your-own-time',
      anchor: 'crew-time.my-tab',
      route: '/timekeeping/my-hours',
      module: 'timekeeping',
      title: 'Your own hours',
      body: 'This side is your time and nobody else’s. Each day shows what the office has you down for: Approve it if it’s right, or Dispute it and say what’s wrong.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'open-crew-hours',
      anchor: 'crew-time.crew-tab',
      route: '/timekeeping/my-hours',
      module: 'timekeeping',
      title: 'Now your crew',
      body: 'Everyone on your crew is under here. The yellow number is how many days are still waiting on you, and it drops as you work through them.',
      // THE STEP THE WHOLE SECOND HALF HANGS ON. Crew Hours is a useState tab, not a URL, so the
      // engine's route sync cannot reach it — the only way to steps 5-8 is this click actually
      // landing. On the old click gate, a press that missed advanced the tour anyway and the four
      // steps after it pointed at a crew panel that was never opened; each of them then waited out
      // its own four-second anchor deadline and skipped, which is the tour "running itself forward"
      // with nobody touching it. It now waits for the crew panel to be on screen.
      advanceOn: ADVANCE_ON.DO,
      until: { appears: 'crew-time.summary' },
      stuckHint: 'No Crew Hours tab? Then this login is not set up as a crew lead, and there is nothing here to sign off.',
    },
    {
      id: 'crew-at-a-glance',
      anchor: 'crew-time.summary',
      route: '/timekeeping/my-hours',
      screen: CREW_TAB,
      module: 'timekeeping',
      title: 'Your crew’s week',
      body: 'Hours the crew worked, how many days still need you, and how many you’ve done. Nothing appears here until the office has reviewed the daily report it came from, so an empty week usually means they haven’t got to it yet.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'one-card-each',
      anchor: 'crew-time.employee',
      route: '/timekeeping/my-hours',
      screen: CREW_TAB,
      module: 'timekeeping',
      title: 'One card per person',
      body: 'Their name, then their week: normal hours, anything over 40 counted as overtime, and every job they put time against. Read it before you approve — this is the record of what they worked.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'check-each-day',
      anchor: 'crew-time.approve-day',
      route: '/timekeeping/my-hours',
      screen: CREW_TAB,
      module: 'timekeeping',
      title: 'Go line by line if you need to',
      body: 'Every line is one day on one job, and this approves just that line. If a line looks wrong, leave it alone and tell the office — once hours have been reviewed, you can’t edit them yourself any more.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'sign-off',
      anchor: 'crew-time.approve-all',
      route: '/timekeeping/my-hours',
      screen: CREW_TAB,
      module: 'timekeeping',
      title: 'Sign the whole card off',
      body: 'When a person’s week looks right, this approves every day of theirs that’s still outstanding. The office sees each one turn green, so they know you’ve checked it and not just left it.',
      // The only step on this tour that writes, and the last one, so it waits for the write rather
      // than for the press. On a practice run the request is answered inside the browser and never
      // reaches the server — the person still sees the days turn green, and nobody's hours are
      // actually signed for.
      advanceOn: ADVANCE_ON.DO,
      until: { requested: 'POST /employee-hours/crew' },
      stuckHint: 'If this person’s week is already all green there is nothing left to approve — skip on.',
    },
    /**
     * A CLOSING STEP, AND IT IS NOT DECORATION.
     *
     * `sign-off` above is gated on the REQUEST, so it is satisfied the instant the approval is
     * emitted — and if it were the last step, the tour would end there. Ending a tour switches
     * practice mode off, and the control still lit under the spotlight is Approve All, which by then
     * has re-pointed at the NEXT person with hours outstanding (WeeklyTimeView's `tourExample`).
     * One more press — the press the reader has spent eight steps being taught to make — would be a
     * genuine sign-off on a real person's paid hours, one click after the card said nothing was
     * saved. So the tour moves the light onto something inert before it ends.
     */
    {
      id: 'thats-the-week',
      anchor: 'crew-time.crew-list',
      route: '/timekeeping/my-hours',
      screen: CREW_TAB,
      module: 'timekeeping',
      title: 'That is the whole job',
      body: 'The count on this tab is how many of your crew’s days are still waiting on you. Come back at the end of each week and clear it — the office reads an unsigned day as one nobody has checked, and they will come and ask you rather than guess.',
      advanceOn: ADVANCE_ON.NEXT,
    },
  ],
})
